import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  MONTH_NAMES_RU,
  TRACKING_START_ABS,
  absMonth,
  ymKey,
  computeStatus,
  STATUS_META,
  computeDueNow,
} from "../lib/months";
import ExpenseForm from "../components/ExpenseForm";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  Plus,
  CaretLeft,
  CaretRight,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  Clock,
  Warning,
  CalendarBlank,
  Wallet,
} from "@phosphor-icons/react";

const STATUS_ICON = {
  paid: CheckCircle,
  waiting: Clock,
  soon: Warning,
  overdue: XCircle,
  future: Clock,
};

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const now = new Date();
  const initialAbs = Math.max(
    absMonth(now.getFullYear(), now.getMonth()),
    TRACKING_START_ABS + 1
  );
  const [viewYear, setViewYear] = useState(Math.floor(initialAbs / 12));
  const [viewMonth, setViewMonth] = useState(initialAbs % 12);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, subRes] = await Promise.all([
        api.get("/expenses"),
        api.get("/subscriptions"),
      ]);
      setItems(expRes.data);
      setSubs(subRes.data);
    } catch (e) {
      console.error("load expenses failed", e);
      toast.error("Не удалось загрузить список расходов");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { currentKey, prevKey, prevInfo, prevDisabled } = useMemo(() => {
    const currentKey = ymKey(viewYear, viewMonth);
    const prevDate = new Date(viewYear, viewMonth - 1, 1);
    const prevInfo = { year: prevDate.getFullYear(), month: prevDate.getMonth() };
    const prevKey = ymKey(prevInfo.year, prevInfo.month);
    const prevDisabled = absMonth(prevInfo.year, prevInfo.month) <= TRACKING_START_ABS;
    return { currentKey, prevKey, prevInfo, prevDisabled };
  }, [viewYear, viewMonth]);

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goToToday = () => {
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  };

  const togglePaid = async (item, targetMonth) => {
    const currentlyPaid = !!item.payments?.[targetMonth];
    try {
      const { data } = await api.put(`/expenses/${item.id}/payment`, {
        month: targetMonth,
        paid: !currentlyPaid,
      });
      setItems((prev) => prev.map((s) => (s.id === item.id ? data : s)));
      toast.success(
        !currentlyPaid
          ? `${item.name} — отмечено как оплачено`
          : `${item.name} — отметка оплаты снята`
      );
    } catch (e) {
      console.error("toggle expense paid failed", e);
      toast.error("Не удалось обновить статус");
    }
  };

  const onDelete = (item) => {
    setConfirm({
      title: "Удалить расход?",
      description: `«${item.name}» — все отметки оплат будут удалены.`,
      confirmLabel: "Удалить",
      tone: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/expenses/${item.id}`);
          setItems((prev) => prev.filter((s) => s.id !== item.id));
          toast.success("Удалено");
        } catch (e) {
          console.error("delete expense failed", e);
          toast.error("Ошибка удаления");
        }
      },
    });
  };

  const onSave = async (formData) => {
    try {
      if (editing) {
        const { data } = await api.put(`/expenses/${editing.id}`, formData);
        setItems((prev) => prev.map((s) => (s.id === data.id ? data : s)));
        toast.success("Обновлено");
      } else {
        const { data } = await api.post("/expenses", formData);
        setItems((prev) => [...prev, data]);
        toast.success("Расход добавлен");
      }
      setFormOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Ошибка сохранения");
    }
  };

  // Stats: total monthly expenses + combined due now (expenses + subscriptions)
  const stats = useMemo(() => {
    const totalExpenses = items.reduce((acc, s) => acc + s.amount, 0);
    const totalSubs = subs.reduce((acc, s) => acc + s.amount, 0);
    const dueExpenses = computeDueNow(items);
    const dueSubs = computeDueNow(subs);
    return {
      total: totalExpenses + totalSubs,
      totalExpenses,
      totalSubs,
      dueExpenses,
      dueSubs,
      dueNow: dueExpenses + dueSubs,
      count: items.length,
    };
  }, [items, subs]);

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label mb-2">СЕКЦИЯ / ФИНАНСЫ</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Ежемесячные расходы</h1>
          <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
            Регулярные расходы аптеки с датами оплаты. «К оплате сейчас» учитывает также «Счета за сайты».
          </p>
        </div>

        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          data-testid="exp-add-button"
          className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150"
        >
          <Plus size={14} weight="bold" />
          Добавить расход
        </button>
      </div>

      <div className="mt-8 bg-white border border-[#d4d4d8] p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => shiftMonth(-1)}
            disabled={prevDisabled}
            data-testid="exp-prev-month"
            title={prevDisabled ? "Учёт начинается с марта 2026" : "Предыдущий месяц"}
            className="p-2 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0a0a0a] disabled:hover:border-[#d4d4d8] disabled:cursor-not-allowed"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <div className="min-w-[260px] text-center">
            <div className="mono-label">ПЕРИОД</div>
            <div className="text-base font-semibold tracking-tight" data-testid="exp-current-month">
              {MONTH_NAMES_RU[prevInfo.month]} {prevInfo.year}
              {" → "}
              {MONTH_NAMES_RU[viewMonth]} {viewYear}
            </div>
          </div>
          <button
            onClick={() => shiftMonth(1)}
            data-testid="exp-next-month"
            className="p-2 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
          >
            <CaretRight size={14} weight="bold" />
          </button>
          <button
            onClick={goToToday}
            className="ml-2 text-xs border border-[#18181b] bg-transparent text-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
          >
            Сегодня
          </button>
        </div>

        <div className="flex items-center gap-6">
          <Stat label="ВСЕГО РАСХОДОВ" value={stats.count} />
          <Stat
            label="К ОПЛАТЕ / МЕС"
            value={`${stats.total.toLocaleString("ru-RU")} ₽`}
            hint={`расходы ${stats.totalExpenses.toLocaleString("ru-RU")} ₽ + сайты ${stats.totalSubs.toLocaleString("ru-RU")} ₽`}
          />
          <Stat
            label="К ОПЛАТЕ СЕЙЧАС"
            value={`${stats.dueNow.toLocaleString("ru-RU")} ₽`}
            accent={stats.dueNow > 0 ? "text-[#FF2A00]" : "text-[#00994C]"}
            testid="exp-stats-due-now"
            hint={
              stats.dueNow > 0
                ? `расходы ${stats.dueExpenses.toLocaleString("ru-RU")} ₽ + сайты ${stats.dueSubs.toLocaleString("ru-RU")} ₽`
                : null
            }
          />
        </div>
      </div>

      {loading && <div className="mt-6 mono-label">ЗАГРУЗКА...</div>}

      {!loading && items.length === 0 && (
        <div className="mt-6 bg-white border border-[#d4d4d8] p-10 text-center" data-testid="exp-empty">
          <Wallet size={32} weight="duotone" className="mx-auto text-[#52525b]" />
          <h3 className="text-lg font-medium mt-3">Расходов пока нет</h3>
          <p className="text-sm text-[#52525b] mt-1">
            Добавьте первый регулярный расход — аренду, коммунальные, зарплаты и т.д.
          </p>
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="mt-4 inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150"
          >
            <Plus size={14} weight="bold" />
            Добавить расход
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-6 border border-[#d4d4d8] bg-white overflow-x-auto" data-testid="exp-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f5f5f4] border-b border-[#d4d4d8]">
                <th className="text-left px-4 py-2.5 mono-label">Расход</th>
                <th className="text-right px-4 py-2.5 mono-label">Сумма</th>
                <th className="text-left px-4 py-2.5 mono-label">Дата оплаты</th>
                <th className="text-center px-4 py-2.5 mono-label border-l border-[#d4d4d8]">
                  {MONTH_NAMES_RU[prevInfo.month]} {prevInfo.year}
                </th>
                <th className="text-center px-4 py-2.5 mono-label border-l border-[#d4d4d8]">
                  {MONTH_NAMES_RU[viewMonth]} {viewYear}
                </th>
                <th className="text-right px-4 py-2.5 mono-label border-l border-[#d4d4d8]">Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const prevStatus = computeStatus(item, prevInfo.year, prevInfo.month);
                const curStatus = computeStatus(item, viewYear, viewMonth);
                const worstMeta =
                  STATUS_META[
                    [prevStatus, curStatus].includes("overdue")
                      ? "overdue"
                      : [prevStatus, curStatus].includes("soon")
                      ? "soon"
                      : curStatus
                  ];
                return (
                  <tr
                    key={item.id}
                    data-testid={`exp-row-${item.id}`}
                    className={`border-b border-[#d4d4d8] last:border-b-0 ${worstMeta.bg} hover:bg-[#f5f5f4] transition-colors duration-150`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.name}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      {item.amount.toLocaleString("ru-RU")} ₽
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <span className="inline-flex items-center gap-1 text-[#52525b]">
                        <CalendarBlank size={12} weight="bold" />
                        {item.invoice_day}-е число
                      </span>
                    </td>
                    <MonthCell
                      item={item}
                      monthKey={prevKey}
                      status={prevStatus}
                      onToggle={togglePaid}
                      testId={`exp-cell-prev-${item.id}`}
                    />
                    <MonthCell
                      item={item}
                      monthKey={currentKey}
                      status={curStatus}
                      onToggle={togglePaid}
                      testId={`exp-cell-current-${item.id}`}
                    />
                    <td className="px-4 py-3 border-l border-[#d4d4d8]">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => { setEditing(item); setFormOpen(true); }}
                          data-testid={`exp-edit-${item.id}`}
                          className="inline-flex items-center gap-1 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
                        >
                          <PencilSimple size={12} weight="bold" />
                          Ред.
                        </button>
                        <button
                          onClick={() => onDelete(item)}
                          data-testid={`exp-delete-${item.id}`}
                          className="p-1.5 text-[#52525b] hover:text-[#FF2A00] hover:bg-[#f5f5f4] transition-colors duration-150"
                          title="Удалить"
                        >
                          <Trash size={14} weight="regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formOpen && (
        <ExpenseForm
          initial={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={onSave}
        />
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Stat({ label, value, accent = "", testid, hint }) {
  return (
    <div className="text-right" data-testid={testid} title={hint || undefined}>
      <div className="mono-label">{label}</div>
      <div className={`font-mono text-lg font-semibold ${accent}`}>{value}</div>
      {hint && <div className="mono-label mt-0.5 normal-case text-[9px] lowercase">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  const Icon = STATUS_ICON[status];
  return (
    <span
      data-testid={`exp-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border ${meta.text} ${meta.border} bg-white`}
    >
      <Icon size={12} weight="fill" />
      {meta.label}
    </span>
  );
}

function MonthCell({ item, monthKey, status, onToggle, testId }) {
  const isPaid = !!item.payments?.[monthKey];
  return (
    <td className="px-4 py-3 border-l border-[#d4d4d8]" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <StatusBadge status={status} />
        <button
          onClick={() => onToggle(item, monthKey)}
          data-testid={`${testId}-toggle`}
          title={isPaid ? "Снять отметку" : "Отметить оплаченным"}
          className={`inline-flex items-center justify-center w-6 h-6 border transition-colors duration-150 shrink-0 ${
            isPaid
              ? "bg-[#00994C] border-[#00994C] text-white hover:bg-[#007a3d]"
              : "bg-white border-[#18181b] hover:bg-[#18181b] hover:text-white"
          }`}
        >
          {isPaid && <CheckCircle size={14} weight="fill" />}
        </button>
      </div>
    </td>
  );
}
